import { z } from 'zod'
import { MonetaryValueSchema } from '@shared/lib/money/money'

export const ReceiptsSyncStatusSchema = z.enum(['pending', 'current', 'stale', 'unavailable'])
export const ReceiptsSyncSchema = z.object({
  lastSyncedAt: z.string().datetime({ offset: true }).nullable(),
  syncStatus: ReceiptsSyncStatusSchema,
})
export const ConnectedAccountScopeSchema = z.object({
  platformId: z.string().min(1),
  accountId: z.string().regex(/^acct_/),
  livemode: z.boolean(),
})
export const ConnectBankDestinationSchema = z
  .object({
    id: z.string().min(1),
    bankName: z.string().nullable(),
    last4: z.string().max(4).nullable(),
  })
  .nullable()
export const ConnectPayoutSchema = z
  .object({
    id: z.string().regex(/^po_/),
    account: ConnectedAccountScopeSchema,
    participant: z.object({ id: z.string(), name: z.string().nullable() }),
    amount: MonetaryValueSchema,
    currency: z.literal('BRL'),
    status: z.enum(['pending', 'in_transit', 'paid', 'failed', 'canceled']),
    createdAt: z.string().datetime({ offset: true }),
    arrivalDate: z.string().datetime({ offset: true }).nullable(),
    destination: ConnectBankDestinationSchema,
    failure: z.object({ code: z.string().nullable(), message: z.string().nullable() }).nullable(),
    compositionStatus: z.enum(['pending', 'complete', 'unavailable', 'divergent']),
    ledgerStatus: z.enum(['pending', 'completed', 'reversed']),
    ledgerPendingReason: z
      .enum(['composition_pending', 'composition_divergent', 'allocation_or_eligibility_unproven'])
      .nullable(),
  })
  .merge(ReceiptsSyncSchema)
export const ConnectMovementSchema = z.object({
  id: z.string().regex(/^txn_/),
  account: ConnectedAccountScopeSchema,
  amount: MonetaryValueSchema,
  fee: MonetaryValueSchema,
  net: MonetaryValueSchema,
  currency: z.literal('BRL'),
  type: z.string(),
  reportingCategory: z.string().nullable(),
  status: z.enum(['available', 'pending']),
  createdAt: z.string().datetime({ offset: true }),
  availableOn: z.string().datetime({ offset: true }).nullable(),
  sourceId: z.string().nullable(),
  payoutId: z.string().nullable(),
  saleIds: z.array(z.string()),
})
export const ConnectPayoutPageSchema = ReceiptsSyncSchema.extend({
  items: z.array(ConnectPayoutSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})
export const ConnectMovementPageSchema = ReceiptsSyncSchema.extend({
  items: z.array(ConnectMovementSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})
export const ConnectCompositionPageSchema = ConnectMovementPageSchema.extend({
  payoutId: z.string(),
  compositionStatus: z.enum(['pending', 'complete', 'unavailable', 'divergent']),
})
export type ConnectedAccountScope = z.infer<typeof ConnectedAccountScopeSchema>
export type ConnectPayout = z.infer<typeof ConnectPayoutSchema>
export type ConnectMovement = z.infer<typeof ConnectMovementSchema>
export type ConnectPayoutPage = z.infer<typeof ConnectPayoutPageSchema>
export type ConnectMovementPage = z.infer<typeof ConnectMovementPageSchema>
export type ConnectCompositionPage = z.infer<typeof ConnectCompositionPageSchema>

export type ConnectSync = z.infer<typeof ReceiptsSyncSchema>
